/*
<summary>
  This functional component "UserProfile" provides user setting functionality like changing language with the help "setting" tab props like initial values.
  Developer:Aashish Singh, Created Date:28-Aug-2022, Last Updated By: Mohammad Saquib Khan, Updated Date: 13-Dec-2022
</summary>
<returns>Returns JSX</returns>
*/
import base64 from 'base-64';
import { Field, Form, Formik } from 'formik';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import toast from 'react-hot-toast';
import validAllowedCharacters from '../../../constants/valid-character-password';
import { useStore } from '../../../contexts/store-provider';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { UpdatePasswordValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import LoaderButton from '../../../shared-components/button/loader-button';
import Label from '../../../shared-components/label/label';
import { CloseModalIcon } from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';
import Settings from '../forms/settings';
import './user-profile.css';

interface IProps {
  userName: string;
  userType: string;
  onModalClosed: () => void;
}

const UserProfile: React.FC<IProps> = (props) => {
  const { authStore, userStore } = useStore();
  const { getUserType, getEmail, getUserId, getUserName } = authStore;
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [showHideConfirmPassword, setShowHideConfirmPassword] = useState(false);
  const [showHideChangePassword, setShowHideChangePassword] = useState(false);
  const { UpdatePasswordService, changePasswordState, resetChangePassword } =
    userStore;

  useEffect(() => {
    if (changePasswordState.success) {
      toast.success(formatMessage('updated_success'));
      resetChangePassword();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePasswordState.success]);

  return (
    <Modal
      className="add-profile-modal"
      show={true}
      centered
    >
      <Modal.Header
        className="header-bg"
        data-testid="closebtn"
      >
        <div>
          <h3 className="m-0">{getUserName ? getUserName : ''}</h3>
          <p className="userType">
            {formatMessage(getUserType ? getUserType : '')}
          </p>
        </div>

        <Modal.Title>
          <button
            onClick={props.onModalClosed}
            className="Crossicons"
          >
            <CloseModalIcon />
          </button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <div className="bg-light p-3">
          <div className="form-group change-password">
            <span className="form-check-label">Change Language? </span>
            <BsForm.Check
              type="switch"
              id="custom-switch"
              label={formatMessage('change_password')}
              onClick={() => setShowHideChangePassword(!showHideChangePassword)}
            />
          </div>
        </div> */}
        {/* <div className="reset-password"></div> */}
        <Tabs
          defaultActiveKey={2}
          id="uncontrolled-tab-example"
          className=""
        >
          <Tab
            eventKey={1}
            title={formatMessage('change_password')}
          >
            <Formik
              initialValues={{ OldPassword: '', NewPassword: '' }}
              enableReinitialize
              validationSchema={UpdatePasswordValidateSchema}
              validateOnBlur={false}
              onSubmit={async (values, { resetForm }) => {
                UpdatePasswordService({
                  TenantUserId: getUserId,
                  NewPassword: base64.encode(values.NewPassword),
                  OldPassword: base64.encode(values.OldPassword),
                });
                resetForm();
              }}
            >
              {({ dirty }) => {
                return (
                  <Form className="change-password mt-3 pt-2">
                    <div className="form-group mb-3">
                      <Label
                        required={true}
                        label="old_password"
                      />
                      <div className="showHideOuterDiv">
                        <Field
                          data-testid="testPassword"
                          className="form-control"
                          name="OldPassword"
                          autoComplete="new-password"
                          type={showHidePassword ? 'text' : 'password'}
                          invalidcharacter={[' ']}
                          validcharacter={validAllowedCharacters}
                          placeholder=""
                          as={FormikFormInput}
                        />
                        {showHidePassword ? (
                          <svg
                            type="button"
                            onClick={() => {
                              setShowHidePassword(!showHidePassword);
                            }}
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="showHidePass css-i6dzq1 modalBox"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle
                              cx="12"
                              cy="12"
                              r="3"
                            ></circle>
                          </svg>
                        ) : (
                          <svg
                            type="button"
                            onClick={() => {
                              setShowHidePassword(!showHidePassword);
                            }}
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="showHidePass css-i6dzq1 modalBox"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line
                              x1="1"
                              y1="1"
                              x2="23"
                              y2="23"
                            ></line>
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="form-group mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="new_password"
                      />
                      <div className="showHideOuterDiv">
                        <Field
                          data-testid="testConfirmPassword"
                          className={`form-control ${showHidePassword === showHideConfirmPassword ? '' : ''}`}
                          // className={`form-control ${showHidePassword === showHideConfirmPassword ? 'border border-success-subtle' : ''}`}
                          name="NewPassword"
                          autoComplete="confirm-new-password"
                          type={showHideConfirmPassword ? 'text' : 'password'}
                          invalidcharacter={[' ']}
                          validcharacter={validAllowedCharacters}
                          placeholder=""
                          as={FormikFormInput}
                        />
                        {showHideConfirmPassword ? (
                          <svg
                            type="button"
                            onClick={() => {
                              setShowHideConfirmPassword(
                                !showHideConfirmPassword
                              );
                            }}
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="showHidePass css-i6dzq1 modalBox"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle
                              cx="12"
                              cy="12"
                              r="3"
                            ></circle>
                          </svg>
                        ) : (
                          <svg
                            type="button"
                            onClick={() => {
                              setShowHideConfirmPassword(
                                !showHideConfirmPassword
                              );
                            }}
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="showHidePass css-i6dzq1 modalBox"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line
                              x1="1"
                              y1="1"
                              x2="23"
                              y2="23"
                            ></line>
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="btn-save text-center">
                      <LoaderButton
                        type="submit"
                        className="btn btn-primary"
                        // isLoading={changePasswordState.inProgress}
                        text="save"
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Tab>
          <Tab
            eventKey={2}
            title="Language"
          >
            {/* <Button variant="primary" type="submit" className="w-100 mt-3">
          <FormattedMessage id="update_password" />
        </Button> */}
            <Settings modalClosed={props.onModalClosed} />
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default observer(UserProfile);
